import Vue from 'vue'
import Router from 'vue-router'
import AuthService from './views/dashboard/AuthService.js'

Vue.use(Router)

// scrollBehavior:
// - only available in html5 history mode
// - defaults to no scroll behavior
// - return false to prevent scroll
export const scrollBehavior = function (to, from, savedPosition) {
  const position = {}
  position.x = 0
  position.y = 0

  if (to.name !== 'Dashboard') {
    return position
  }

  if (savedPosition) {
    // savedPosition is only available for popstate navigations.
    return savedPosition
  } else {
    // scroll to anchor by returning the selector
    if (to.hash) {
      position.selector = to.hash
      // specify offset of the elements
      position.offset = { y: 25 }
      if (/^#\d/.test(to.hash) || document.querySelector(to.hash)) {
        return position
      }
      // if the returned position is falsy or an empty object,
      // will retain current scroll position.
      return false
    }
    return position
  }
}

const router = new Router({
  mode: 'hash',
  base: process.env.VUE_APP_ENV_URL,
  scrollBehavior,
  routes: [
    {
      // Toutes les pages de connexion
      path: '/auth',
      component: () => import('@/views/auth/Index'),
      children: [
        // Page de connexion
        {
          name: 'Login',
          path: 'login',
          component: () => import('@/views/auth/LoginHQ'),
          props: true,
          meta: { requiresNotAuth: true },
        },
        // Page de déconnexion
        {
          name: 'Logout',
          path: 'logout',
          component: () => import('@/views/auth/Logout'),
        },
        // Page d'inscription
        {
          name: 'Register',
          path: 'register',
          component: () => import('@/views/auth/Register'),
          meta: { requiresNotAuth: true },
        },
        // Page de mot de passe oublié
        {
          name: 'Forgotten Password',
          path: 'forgottenPassword',
          component: () => import('@/views/auth/ForgottenPassword'),
          meta: { requiresNotAuth: true },
        },
        // Page de nouveau mot de passe
        {
          name: 'New Password',
          path: 'newpassword/:email/:token',
          component: () => import('@/views/auth/NewPassword'),
          props: true,
        },
        // Page de redirection après définition d'un nouveau mot de passe
        {
          name: 'Redirection New Password',
          path: 'redirectnewpassword',
          component: () => import('@/views/auth/RedirectionNewPassword'),
        },
      ],
    },
    {
      // Toutes les pages d'administration
      path: '/admin',
      component: () => import('@/views/dashboard/Index'),
      children: [
        // Liste des entreprises
        {
          name: 'Companies List',
          path: 'companies',
          component: () => import('@/views/dashboard/tables/CompaniesList'),
        },
        // Liste des mondes
        {
          name: 'Worlds List',
          path: 'worlds',
          component: () => import('@/views/dashboard/tables/WorldsList'),
        },
        // Liste des mondes par entreprise
        {
          name: 'Company Worlds List',
          path: 'companyworlds/:nameCompany/:idCompany',
          component: () => import('@/views/dashboard/tables/CompanyWorldsList'),
          props: true,
        },
        // Liste des tresors par monde
        {
          name: 'World Treasures List',
          path: 'worldtreasures/:nameWorld/:idWorld',
          component: () => import('@/views/dashboard/tables/WorldTreasuresList'),
          props: true,
        },
        // Liste des jeux par monde
        {
          name: 'World Games List',
          path: 'worldgames/:nameWorld/:idWorld',
          component: () => import('@/views/dashboard/tables/WorldGamesList'),
          props: true,
        },
        // Liste des ressources par monde
        {
          name: 'World Resources List',
          path: 'worldresources/:nameWorld/:idWorld',
          component: () => import('@/views/dashboard/tables/WorldResourcesList'),
          props: true,
        },
        // Tableaux de bord des entreprises
        {
          name: 'Company Dashboard',
          path: 'dashboard/:idCompany',
          component: () => import('@/views/dashboard/Dashboard'),
          props: true,
        },
        // Liste des users
        {
          name: 'Users',
          path: 'users',
          component: () => import('@/views/dashboard/tables/UsersList'),
          meta: { requiresAuth: true },
        },
      ],
    },
    {
      path: '/',
      component: () => import('@/views/dashboard/Index'),
      children: [
        // Tableau de bord principal
        {
          name: 'Dashboard',
          path: '',
          component: () => import('@/views/dashboard/Dashboard'),
          props: true,
          meta: { requiresAuth: true },
        },
        // Liste des labels
        {
          name: 'Tags List',
          path: 'tags',
          component: () => import('@/views/dashboard/tables/TagsList'),
          meta: { requiresAuth: true },
        },
      ],
    },
    {
      // Pages d'erreur
      path: '*',
      component: () => import('@/views/auth/Index'),
      children: [
        // Page d'erreur 404 Not Found
        {
          name: '404 Error',
          path: '',
          component: () => import('@/views/auth/Error'),
        },
      ],
    },
  ],
})

const Auth = new AuthService()

// Reset the anchors after a refresh
router.afterEach((to, from) => {
  if (from.name === null && to.name === 'Dashboard' && to.hash !== '') {
    router.replace({ path: '/' })
  }
})

router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    // Redirection des pages nécessitant une connexion vers la page de connexion
    if (!Auth.loggedIn()) {
      next({
        path: '/auth/login',
        query: {
          redirect: to.fullPath,
        },
      })
    } else {
      next()
    }
  } else if (to.matched.some(record => record.meta.requiresNotAuth)) {
    // Redirection des pages nécessitant une absence de connexion vers la page d'accueil
    if (Auth.loggedIn()) {
      next({
        path: '/',
        query: {
          redirect: to.fullPath,
        },
      })
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router
